import { Alert, Box, Grid, Stack, Typography } from '@mui/material'
import React, { useEffect, useMemo, useState } from 'react'
import API from '../../../../services/API'
import CustomDataGrid from '../../../../components/DataGrid/CustomDataGrid'
import moment from 'moment'
import NumberCardTwo from '../../../../components/Cards/NumberCardTwo'
import { useNavigate, useSearchParams } from 'react-router-dom'
import PriorityHigh from '@mui/icons-material/PriorityHigh'

const EXPIRY_PARAM_TO_DATE = {
  today: () => moment().startOf('day').toISOString(),
  '30days': () => moment().add(30, 'days').endOf('day').toISOString(),
  '3months': () => moment().add(3, 'months').endOf('day').toISOString(),
}

const ViewAll = () => {
  const [medicines, setMedicines] = useState([])
  const [loading, setLoading] = useState(true)
  const [fetchError, setFetchError] = useState(false)

  const [expiredCount, setExpiredCount] = useState(0)
  const [expiringMonthCount, setExpiringMonthCount] = useState(0)
  const [expiring3MonthCount, setExpiring3MonthCount] = useState(0)

  const [sortModel, setSortModel] = useState([{ field: 'expiry', sort: 'asc' }])
  const [filterModel, setFilterModel] = useState({
    items: [],
  })
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  })

  const [totalCount, setTotalCount] = useState(0)

  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  useEffect(() => {
    API.get('/medsmx/meds-headers').then((res) => {
      if (res.status === 200) {
        setExpiredCount(res.data.totalExpiredMeds)
        setExpiringMonthCount(res.data.expiringThisMonth)
        setExpiring3MonthCount(res.data.expiring3Months)
      }
    })
  }, [])

  useEffect(() => {
    setLoading(true)
    setFetchError(false)

    API.post('/medsmx/availablemeds', {
      page: paginationModel.page + 1,
      limit: paginationModel.pageSize,
      sortModel,
      filterModel,
    })
      .then((res) => {
        if (res.status === 200) {
          setMedicines(res.data.meds || [])
          setTotalCount(res.data.totalCount || 0)
        }
      })
      .catch(() => setFetchError(true))
      .finally(() => setLoading(false))
  }, [paginationModel, filterModel, sortModel])

  const columns = useMemo(
    () => [
      {
        field: 'medicine',
        headerName: 'Medicine',
        flex: 1,
        minWidth: 250,
        valueGetter: (value, row) => row.medicine?.title || '',
      },
      {
        field: 'constitution',
        headerName: 'Constitution',
        minWidth: 100,
        valueGetter: (value, row) => row.medicine?.constitution || '',
      },
      {
        field: 'presentation',
        headerName: 'Presentation',
        minWidth: 200,
        valueGetter: (value, row) => row.medicine?.presentation || '',
      },
      {
        field: 'route',
        headerName: 'Route',
        minWidth: 100,
        valueGetter: (value, row) => row.medicine?.route || '',
      },
      {
        field: 'location',
        headerName: 'Location',
        minWidth: 150,
        valueGetter: (value, row) => row.location?.name || '',
      },
      {
        field: 'batchNumber',
        headerName: 'Batch Number',
        minWidth: 150,
      },
      {
        field: 'expiry',
        headerName: 'Expiry Date',
        type: 'date',
        minWidth: 150,
        valueGetter: (value, row) => (row.expiry ? moment(row.expiry).toDate() : null),
        renderCell: (params) => {
          const isExpired = moment().startOf('day').isAfter(moment(params.row.expiry))

          return (
            <Box
              width="100%"
              height="100%"
              bgcolor={isExpired ? 'red' : 'transparent'}
              color={isExpired ? 'white' : 'black'}
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Box display="flex" flexDirection="row" gap={1} alignItems="center">
                {moment(params.row.expiry).format('DD/MM/YYYY')}
                {isExpired && <PriorityHigh sx={{ color: 'white' }} />}
              </Box>
            </Box>
          )
        },
      },
    ],
    []
  )

  const applyQuickExpiryFilter = (value) => {
    setFilterModel({
      items: [
        {
          field: 'expiry',
          operator: 'onOrBefore',
          value,
        },
      ],
    })
    setPaginationModel((prev) => ({ ...prev, page: 0 }))
  }

  // Lets the Medicines Management landing page (index.jsx) deep-link
  // straight into one of these same quick filters instead of only being
  // reachable by clicking a card on this page.
  useEffect(() => {
    const expiryParam = searchParams.get('expiry')
    const toDate = EXPIRY_PARAM_TO_DATE[expiryParam]
    if (toDate) applyQuickExpiryFilter(toDate())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Stack>
      <Typography variant="title2">View All Medicines</Typography>
      <Typography variant="caption">
        Clicking on a row will take you to that medicine&apos;s current location
        where you can perform more actions.
      </Typography>

      <Grid container spacing={2}>
        <NumberCardTwo
          lg={4}
          number={expiredCount}
          label="Expired Medicines"
          color={expiredCount > 0 ? 1 : 9}
          sx={{ cursor: 'pointer' }}
          onClick={() =>
            applyQuickExpiryFilter(moment().startOf('day').toISOString())
          }
        />

        <NumberCardTwo
          lg={4}
          number={expiringMonthCount}
          label="Expiring within 30 days"
          color={expiringMonthCount > 0 ? 6 : 9}
          sx={{ cursor: 'pointer' }}
          onClick={() =>
            applyQuickExpiryFilter(
              moment().add(30, 'days').endOf('day').toISOString()
            )
          }
        />

        <NumberCardTwo
          lg={4}
          number={expiring3MonthCount}
          label="Expiring within 3 months"
          color={expiring3MonthCount > 0 ? 6 : 9}
          sx={{ cursor: 'pointer' }}
          onClick={() =>
            applyQuickExpiryFilter(
              moment().add(3, 'months').endOf('day').toISOString()
            )
          }
        />

        <Grid item xs={12} paddingTop={2}>
          <CustomDataGrid
            rows={medicines}
            columns={columns}
            rowCount={totalCount}
            paginationMode="server"
            sortingMode="server"
            filterMode="server"
            error={fetchError}
            loading={loading}
            paginationModel={paginationModel}
            sortModel={sortModel}
            filterModel={filterModel}
            getRowId={(row) => row._id}
            disableRowSelectionOnClick
            onPaginationModelChange={setPaginationModel}
            onSortModelChange={(model) => {
              setSortModel(model)
              setPaginationModel((prev) => ({ ...prev, page: 0 }))
            }}
            onFilterModelChange={(model) => {
              setFilterModel(model)
              setPaginationModel((prev) => ({ ...prev, page: 0 }))
            }}
            onRowClick={(params) =>
              navigate('/medicines-management/' + params.row.location?._id)
            }
            slots={{
              noRowsOverlay: () => (
                <Box
                  width="100%"
                  height="100%"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                >
                  <Alert severity={fetchError ? 'error' : 'info'}>
                    {fetchError
                      ? 'Unable to fetch data right now. Please try again later.'
                      : 'No medicines in stock yet. Build your formulary and add stock to a medicine store under Site Configuration → Governance → Medicines — every batch then appears here with its expiry tracked.'}
                  </Alert>
                </Box>
              ),
            }}
          />
        </Grid>
      </Grid>
    </Stack>
  )
}

export default ViewAll